import { useMemo, useRef } from 'react';
import { Group, Layers, Vector3Tuple } from 'three';

import { GroupProps } from '@react-three/fiber';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { CameraPath as CameraPathImpl, useSelection } from '@spazious/dollhouse-engine';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { DOLLHOUSE_LAYER_DEFAULT } from '@spazious/dollhouse-three';
import { ToolModeEnum } from '@spazious/dollhouse-types';

import { useFeatures } from '../Features/index.js';

export interface CameraPathProps extends GroupProps {
  points?: [Vector3Tuple, Vector3Tuple][];
  fcarpet?: number;
  attrs?: Record<string, any>;
  selector: string;
}

export const CameraPath: React.FC<CameraPathProps> = ({
  points = [],
  selector,
  children,
  visible = true,
  ...restProps
}) => {
  const { staging, createAction } = useStaging();
  const { hasFeature } = useFeatures();
  const tool = useStore(({ tool }) => tool);
  const ref = useRef<Group | null>(null);
  const { select, selected } = useSelection();

  const layers = useMemo(() => {
    const layers = new Layers();
    layers.enable(DOLLHOUSE_LAYER_DEFAULT);
    return layers;
  }, []);

  const disabled = !hasFeature('graphs.setup.node.camerapath:editable');
  const isVisible = visible && visible && tool === ToolModeEnum.CameraPath;
  const isSelected = useMemo(() => selected.some((obj) => obj === ref.current), [selected]);

  return (
    <group ref={ref} layers={layers} visible={isVisible} {...restProps}>
      {children}
      {points.length && isVisible && (
        <CameraPathImpl
          color={isSelected ? 'red' : 'blue'}
          disabled={disabled}
          points={points}
          onSelect={() => {
            if (ref.current) {
              select([ref.current]);
            }
          }}
          onUpdate={({ points: newPoints, first, last }) => {
            if (first) {
              staging.prepare();
            }
            staging.add(
              createAction<[Vector3Tuple, Vector3Tuple][]>('update', selector, 'points', newPoints)
            );
            if (last) {
              staging.commit(CommanderActionTypesEnum.Update);
            }
          }}
        />
      )}
    </group>
  );
};
